import { MatchState } from '../types';

/**
 * Retourne le côté opposé pour le service
 */
function getOppositeSide(side: 'left' | 'right'): 'left' | 'right' {
  return side === 'left' ? 'right' : 'left';
}

/**
 * Trouve le premier point réel d'un jeu (celui avec le plus petit score total > 0)
 */
function findFirstRealPoint(gamePoints: any[]) {
  // Chercher le point avec le plus petit score total > 0
  let firstRealPoint = null;
  let minScoreTotal = Infinity;
  
  for (const point of gamePoints) {
    const totalScore = point.scoreA + point.scoreB;
    if (totalScore > 0 && totalScore < minScoreTotal) {
      minScoreTotal = totalScore;
      firstRealPoint = point;
    }
  }
  
  return firstRealPoint;
}

/**
 * Récupère l'historique complet de tous les points de tous les jeux
 * À partir de l'historique du matchState
 */
export function getAllMatchPoints(matchState: MatchState) {
  const players = {
    A: matchState.matchInfo.playerA,
    B: matchState.matchInfo.playerB
  };

  const allPoints: any[] = [];
  const pointsPerGame: { [gameNumber: number]: any[] } = {};

  // 🔍 Parcourir TOUT l'historique pour récupérer les points
  if (matchState.history && matchState.history.length > 0) {
    matchState.history.forEach((historyState, historyIndex) => {
      if (historyState.pointsTable && historyState.pointsTable.length > 0) {
        const gameNumber = historyState.gameResults ? historyState.gameResults.length + 1 : 1;
        
        // Stocker les points par jeu pour éviter les doublons
        if (!pointsPerGame[gameNumber] || historyState.pointsTable.length > pointsPerGame[gameNumber].length) {
          pointsPerGame[gameNumber] = historyState.pointsTable.map((point, index) => ({
            pointNumber: index + 1,
            scoreA: point.playerA,
            scoreB: point.playerB,
            servingPlayer: point.servingPlayer,
            servingPlayerName: point.servingPlayer
              ? (point.servingPlayer === 'A' ? players.A : players.B)
              : null,
            servingSide: point.servingSide,
            decision: point.decision || null,
            decisionFor: point.decisionFor || null,
            decisionForName: point.decisionFor
              ? (point.decisionFor === 'A' ? players.A : players.B)
              : null,
            gameNumber: gameNumber
          }));
        }
      }
    });
  }

  // 🔍 Ajouter les points du jeu en cours s'il n'est pas déjà dans l'historique
  if (matchState.pointsTable && matchState.pointsTable.length > 0) {
    const currentGameNumber = matchState.gameResults.length + 1;
    
    if (!pointsPerGame[currentGameNumber] && !matchState.matchEnded) {
      pointsPerGame[currentGameNumber] = matchState.pointsTable.map((point, index) => ({
        pointNumber: index + 1,
        scoreA: point.playerA,
        scoreB: point.playerB,
        servingPlayer: point.servingPlayer,
        servingPlayerName: point.servingPlayer
          ? (point.servingPlayer === 'A' ? players.A : players.B)
          : null,
        servingSide: point.servingSide,
        decision: point.decision || null,
        decisionFor: point.decisionFor || null,
        decisionForName: point.decisionFor
          ? (point.decisionFor === 'A' ? players.A : players.B)
          : null,
        gameNumber: currentGameNumber
      }));
    }
  }

  // Pour chaque jeu terminé, ajouter le point gagnant manquant
  matchState.gameResults.forEach((gameResult, gameIndex) => {
    const gameNumber = gameIndex + 1;
    const gamePoints = pointsPerGame[gameNumber] || [];
    
    // Si on a des points pour ce jeu
    if (gamePoints.length > 0) {
      const lastPoint = gamePoints[gamePoints.length - 1];
      const winner = gameResult.winner;
      
      // Vérifier si le dernier point n'atteint pas le score gagnant
      const lastWinnerScore = winner === 'A' ? lastPoint.scoreA : lastPoint.scoreB;
      const finalWinnerScore = winner === 'A' ? gameResult.scoreA : gameResult.scoreB;
      
      if (lastWinnerScore < finalWinnerScore) {
        // Le gagnant était forcément le serveur, donc il change de côté
        const newServingSide = lastPoint.servingSide === 'left' ? 'right' : 'left';
        
        // Ajouter le point gagnant manquant
        const winningPoint = {
          pointNumber: gamePoints.length + 1,
          scoreA: gameResult.scoreA,
          scoreB: gameResult.scoreB,
          servingPlayer: winner,
          servingPlayerName: winner === 'A' ? players.A : players.B,
          servingSide: newServingSide,
          decision: null,
          decisionFor: null,
          decisionForName: null,
          gameNumber: gameNumber
        };
        pointsPerGame[gameNumber].push(winningPoint);
      }
    }
  });

  // 🔗 NOUVEAU: Ajouter les points 0 pour chaque jeu
  Object.keys(pointsPerGame).forEach(gameNumberStr => {
    const gameNumber = parseInt(gameNumberStr);
    const gamePoints = pointsPerGame[gameNumber];
    
    // Si le jeu a des points
    if (gamePoints.length > 0) {
      // Trouver le premier point réel (pas forcément à l'index 0 à cause du point gagnant ajouté)
      const firstRealPoint = findFirstRealPoint(gamePoints);
      
      // Si on a trouvé un premier point réel avec un côté de service défini
      if (firstRealPoint && firstRealPoint.servingSide) {
        // Créer le point 0 avec le côté opposé
        const point0 = {
          pointNumber: 0,
          scoreA: 0,
          scoreB: 0,
          servingPlayer: firstRealPoint.servingPlayer,
          servingPlayerName: firstRealPoint.servingPlayerName,
          servingSide: getOppositeSide(firstRealPoint.servingSide),
          decision: null,
          decisionFor: null,
          decisionForName: null,
          gameNumber: gameNumber
        };
        
        // Insérer le point 0 en première position
        pointsPerGame[gameNumber].unshift(point0);
      }
    }
  });

  // 🔗 Assembler tous les points dans l'ordre chronologique
  Object.keys(pointsPerGame)
    .sort((a, b) => parseInt(a) - parseInt(b))
    .forEach(gameNumber => {
      pointsPerGame[parseInt(gameNumber)].forEach(point => {
        allPoints.push({
          ...point,
          globalPointNumber: allPoints.length + 1
        });
      });
    });

  return allPoints;
}